import { z } from "zod";

// A point-in-time blood-glucose reading (slice 3, metabolic correlation).
// Special-category health data (GDPR Art. 9 / nLPD): per-user partition,
// never aggregated, never logged, wiped on account delete. Same posture as
// Measurement. Imported from Health Connect (BloodGlucose) or entered by hand.
//
// Stored as its own DDB row (pk=USER#<sub>, sk=GLUCOSE#<13-digit-ms>#<id>),
// a time-series entity like Session/Measurement so a date-window read is an
// O(window) range query.
//
// Canonical unit is mg/dL (the unit the family reads off the meter/CGM); HC
// carries both mg/dL and mmol/L, we persist mg/dL and convert at render.

// 20–600 mg/dL spans hypo to severe hyper; rules out a 0 glitch and a
// mmol-entered-as-mg slip (mmol values are ~3–20). Rounded to an integer at the
// edge: meters/CGMs report integer mg/dL, but a producer that converts from
// mmol/L (cgm-watch) or an HC import (which carries the mmol→mg/dL float) would
// otherwise persist a fractional value — which then round-trips to HC as an
// over-precise mmol reading (e.g. 6.75431). Single source of truth: every
// ingest path stores integer mg/dL, and a legacy float row reads back rounded.
const MgDl = z
  .number()
  .min(20)
  .max(600)
  .transform((n) => Math.round(n));

export const GlucoseReading = z.object({
  id: z.uuid(),
  userSub: z.string().min(1).max(128),
  takenAt: z.iso.datetime(),
  mgDl: MgDl,
  // HC context (optional): how the reading relates to eating. Stored as the
  // HC label string (e.g. "fasting", "before_meal", "after_meal") when present.
  mealType: z.string().min(1).max(32).optional(),
  relationToMeal: z.string().min(1).max(32).optional(),
  note: z.string().max(500).optional(),
  // Provenance. Absent ⇒ JL-entered. "hc:<pkg>" for a Health Connect import.
  // externalId = HC record id (dedup/idempotency). Set on import, not patched.
  source: z.string().min(1).max(96).optional(),
  externalId: z.string().min(1).max(256).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type GlucoseReading = z.infer<typeof GlucoseReading>;

// What a display surface actually needs from a reading: the value and WHEN it
// happened. A full GlucoseReading satisfies it structurally, and so does a
// not-yet-persisted one held in the offline outbox — which is the point: the
// quick card must be able to show a reading the user just took offline without
// fabricating the server-owned fields (userSub/createdAt/updatedAt) to satisfy
// a type. Inventing those would be a denormalised snapshot that can drift from
// the real row ([[feedback_no_denormalized_display_snapshots]]).
//
// `takenAt` is non-negotiable here (HM-1, 2026-07-15): QuickGlucoseCard used to
// take a bare `latestMgDl: number`, so the primitive COULD NOT express "old"
// and rendered the most recent reading that ever existed as «Latest: N» with no
// date, forever. A value without its instant is not a reading, it's a rumour.
export type GlucoseSample = Pick<GlucoseReading, "mgDl" | "takenAt">;

// Client → server payload. Server fills userSub/createdAt/updatedAt. id is
// optional but a UUID when present (offline-first idempotency).
export const NewGlucoseReading = GlucoseReading.omit({
  id: true,
  userSub: true,
  createdAt: true,
  updatedAt: true,
}).extend({ id: z.uuid().optional() });
export type NewGlucoseReading = z.infer<typeof NewGlucoseReading>;
